import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import type { DemoMessageItem } from '../../api/demo';

export interface DemoEvidenceItem {
  messageId: string;
  logicalPath: string;
  hash: string | null;
  sourceType?: string | null;
}

interface DemoEvidencePanelProps {
  evidence: DemoEvidenceItem[];
  sourceHash?: string | null;
  messages?: DemoMessageItem[];
  onViewMessage?: (message: DemoMessageItem) => void;
}

function shortHash(value: string | null | undefined): string {
  if (!value) return '—';
  return value.slice(0, 12);
}

/**
 * Provenienza dell'analisi e consultazione del messaggio email originale.
 */
export function DemoEvidencePanel({
  evidence,
  sourceHash,
  messages,
  onViewMessage,
}: DemoEvidencePanelProps) {
  return (
    <div className="space-y-3">
      {evidence.length > 0 ? (
        <ul className="space-y-2">
          {evidence.map((item, index) => {
            const matchingMessage =
              messages?.find(
                (m) =>
                  (m.message_id && m.message_id === item.messageId) ||
                  (m.logical_path && m.logical_path === item.logicalPath),
              ) || (messages && messages.length > 0 ? messages[0] : null);

            return (
              <li
                key={`${item.messageId}-${item.logicalPath}-${index}`}
                data-testid={`demo-evidence-${index}`}
                className="rounded-lg border border-border p-3 transition-colors hover:bg-muted/30"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      data-testid="demo-evidence-message-id"
                      className="break-all font-mono text-xs font-semibold text-foreground"
                    >
                      {item.messageId}
                    </span>
                    {matchingMessage?.subject && (
                      <span className="text-xs font-medium text-foreground">
                        ({matchingMessage.subject})
                      </span>
                    )}
                    {item.sourceType && (
                      <Badge variant="outline" className="text-muted-foreground">
                        {item.sourceType}
                      </Badge>
                    )}
                  </div>

                  {onViewMessage && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1.5 text-primary border-primary/30 hover:bg-primary/10"
                      onClick={() => matchingMessage && onViewMessage(matchingMessage)}
                      disabled={!matchingMessage}
                    >
                      <Mail className="size-3 text-primary" />
                      Visualizza email
                    </Button>
                  )}
                </div>
                <div
                  data-testid="demo-evidence-logical-path"
                  className="mt-1 break-all font-mono text-xs text-muted-foreground"
                >
                  {item.logicalPath}
                  {matchingMessage?.from_address &&
                    ` · Da: ${matchingMessage.from_address}`}
                  {matchingMessage?.received_at &&
                    ` · Data: ${matchingMessage.received_at}`}
                </div>
                <div
                  data-testid="demo-evidence-hash"
                  title={item.hash ?? undefined}
                  className="mt-1 break-all font-mono text-[11px] text-muted-foreground"
                >
                  {shortHash(item.hash)}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="rounded-lg border border-border p-4 text-center text-sm text-muted-foreground">
          Nessuna evidenza disponibile
        </div>
      )}

      {sourceHash && (
        <div className="text-xs text-muted-foreground">
          Hash sorgente:{' '}
          <span data-testid="demo-evidence-source-hash" className="font-mono">
            {shortHash(sourceHash)}
          </span>
        </div>
      )}
    </div>
  );
}
